import { inject } from '@angular/core';
import { AuthentificationService, ContextService } from '@kiraah-shared/core';
import { TranslationManagerService } from '@kiraah-shared/app-skeleton';
import { firstValueFrom } from 'rxjs';

/**
 * APP_INITIALIZER function that ensures the user's profile data
 * is loaded into memory before any component initializes.
 *
 * After a page refresh (F5), localStorage contains the access_token and login_status
 * but the in-memory ContextService.data is empty. This initializer fetches the profile
 * from the backend via GET /api/master/authentication/me (which identifies the user
 * solely via the JWT) and populates the ContextService.
 *
 * Angular guarantees that all initializers resolve before the first
 * component renders, so contextService.data is fully populated when ngOnInit() runs.
 */
export function appInitializer(): Promise<void> {
  const authService = inject(AuthentificationService);
  const contextService = inject(ContextService);
  const translationManagerService = inject(TranslationManagerService);

  return (async () => {
    // Ensure base translations are loaded before any component renders.
    // This prevents transient untranslated keys during startup/login flows.
    try {
      await translationManagerService.loadAllTranslations('fr');
    } catch (error) {
      console.warn('[AppInit] Failed to preload fr translations, continuing.', error);
    }

    // If not authenticated, nothing to bootstrap
    if (!authService.authenticated) {
      return;
    }

    // If context already loaded (e.g. just logged in, no F5), skip
    if (contextService.data?.Identity?.Uid) {
      return;
    }

    // Proactively refresh the access token if it is expired or near expiry.
    // This covers the case where the token expired between the last proactive
    // refresh and the page reload (F5). The interceptor will also handle 401
    // as a safety net, but refreshing upfront avoids the extra round-trip.
    if (authService.isTokenExpiredOrNearExpiry()) {
      try {
        await firstValueFrom(authService.refreshTokenIfNearExpiry());
      } catch {
        // Refresh failed — still attempt /me so the interceptor can retry on 401.
        // If both fail, we'll clean up below.
        console.warn('[AppInit] Proactive token refresh failed, proceeding with /me');
      }
    }

    // Authenticated but no profile in memory -> re-fetch from server
    try {
      const response = await firstValueFrom(authService.getCurrentProfile());
      if (response.IsSuccessful && response.Data?.ProfilData) {
        contextService.setContextData(response.Data.ProfilData);
      } else {
        // Profile load failed (account disabled, deleted, etc.)
        // Clear auth state and let user re-login
        console.warn('[AppInit] Profile load returned unsuccessful:', response.MessageKey);
        authService.clearAuthenticationState();
        contextService.clearAll();
      }
    } catch (error) {
      // Network error or server error
      console.error('[AppInit] Failed to load profile:', error);
      authService.clearAuthenticationState();
      contextService.clearAll();
    }
  })();
}
